/**
 * P3-31/32: Religion tab 按钮模型 hooks。
 *
 * 复用 Bonfire 的 useButtonModel（每 tick fetchModel 快照订阅）。
 */
import { useCallback, useMemo, type MouseEvent } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import {
  useButtonModel,
  type ModernButtonController,
} from "../bonfire/useButtonModel";

export interface ModernButtonSnapshot {
  name: string;
  description: string;
  visible: boolean;
  enabled: boolean;
  resourceIsLimited: string | boolean;
  /** BuildingStackableBtn 族扩展字段 */
  val?: number;
  on?: number;
  metaName?: string;
  lackResConvert?: boolean;
  togglable?: boolean;
  hasSellLink?: boolean;
  showSellLink?: boolean;
  togglableOnOffLink?: LinkSnapshot;
  toggleAutomationLink?: LinkSnapshot;
}

export interface LinkSnapshot {
  title: string;
  visible?: boolean;
  cssClass?: string;
  tooltip?: string;
  handler: (event: MouseEvent) => void;
}

export interface MultiLinkSnapshot extends ModernButtonSnapshot {
  links: LinkSnapshot[];
}

export interface RefineLinksSnapshot extends ModernButtonSnapshot {
  links: LinkSnapshot[];
}

export interface UseReligionButtonModelResult {
  snap: ModernButtonSnapshot | null;
  modelRef: React.MutableRefObject<Record<string, unknown> | null>;
  controller: ModernButtonController | null;
  getTooltip: () => string;
}

export interface UseMultiLinkButtonModelResult {
  snap: MultiLinkSnapshot | null;
  modelRef: React.MutableRefObject<Record<string, unknown> | null>;
  controller: ModernButtonController | null;
  getTooltip: () => string;
}

type ControllerCtor = new (
  game: GamePage,
  opts?: Record<string, unknown>,
) => ModernButtonController;

function getController(
  getter: () => ControllerCtor | undefined,
  opts?: Record<string, unknown>,
): ModernButtonController | null {
  const game = getGame();
  const Ctor = getter();
  if (!Ctor || !game) {
    return null;
  }
  return new Ctor(game, opts);
}

function toModernSnapshot(model: Record<string, unknown>): ModernButtonSnapshot {
  const meta = (model.metadata ?? {}) as {
    val?: number;
    on?: number;
    name?: string;
    lackResConvert?: boolean;
  };
  const pickLink = (key: string): LinkSnapshot | undefined => {
    const link = model[key] as
      | { title?: string; cssClass?: string; tooltip?: string; handler?: (event: MouseEvent) => void }
      | undefined;
    if (!link) {return undefined;}
    return {
      title: String(link.title ?? ""),
      cssClass: link.cssClass,
      tooltip: link.tooltip,
      handler: (event: MouseEvent) => link.handler?.(event),
    };
  };
  return {
    name: String(model.name ?? ""),
    description: String(model.description ?? ""),
    visible: model.visible !== false,
    enabled: model.enabled !== false,
    resourceIsLimited: (model.resourceIsLimited as string | boolean) ?? "",
    val: meta.val,
    on: meta.on,
    metaName: meta.name,
    lackResConvert: meta.lackResConvert,
    togglable: !!model.togglable,
    hasSellLink: !!model.hasSellLink,
    showSellLink: !!model.showSellLink,
    togglableOnOffLink: pickLink("togglableOnOffLink"),
    toggleAutomationLink: pickLink("toggleAutomationLink"),
  };
}

function linkEq(a: LinkSnapshot | undefined, b: LinkSnapshot | undefined): boolean {
  if (a === b) {return true;}
  if (!a || !b) {return false;}
  return a.title === b.title && a.cssClass === b.cssClass && a.tooltip === b.tooltip;
}

function snapshotEq(a: ModernButtonSnapshot | null, b: ModernButtonSnapshot | null): boolean {
  if (a === b) {return true;}
  if (!a || !b) {return false;}
  return (
    a.name === b.name &&
    a.description === b.description &&
    a.visible === b.visible &&
    a.enabled === b.enabled &&
    a.resourceIsLimited === b.resourceIsLimited &&
    a.val === b.val &&
    a.on === b.on &&
    a.metaName === b.metaName &&
    a.lackResConvert === b.lackResConvert &&
    a.togglable === b.togglable &&
    a.hasSellLink === b.hasSellLink &&
    a.showSellLink === b.showSellLink &&
    linkEq(a.togglableOnOffLink, b.togglableOnOffLink) &&
    linkEq(a.toggleAutomationLink, b.toggleAutomationLink)
  );
}

function linksEq(a: LinkSnapshot[], b: LinkSnapshot[]): boolean {
  if (a.length !== b.length) {return false;}
  for (let i = 0; i < a.length; i++) {
    if (a[i].title !== b[i].title || a[i].visible !== b[i].visible) {return false;}
  }
  return true;
}

function multiLinkSnapshotEq(
  a: MultiLinkSnapshot | null,
  b: MultiLinkSnapshot | null,
): boolean {
  if (a === b) {return true;}
  if (!a || !b) {return false;}
  return (
    snapshotEq(a, b) &&
    linksEq(a.links, b.links)
  );
}

function makeGetTooltip(
  controller: ModernButtonController | null,
  createOpts: () => Record<string, unknown>,
): () => string {
  return () => {
    const game = getGame();
    const helper = window.ButtonModernHelper;
    if (!game || !controller || !helper) {
      return "";
    }
    return helper.getTooltipHTML(controller, { options: createOpts() });
  };
}

// ---------------------------------------------------------------------------
// Praise / Adore / Transcend
// ---------------------------------------------------------------------------

function getPraiseControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    com?: { nuclearunicorn?: { game?: { ui?: { PraiseBtnController?: ControllerCtor } } } };
  };
  return w.com?.nuclearunicorn?.game?.ui?.PraiseBtnController;
}

export function usePraiseButtonModel(
  name: string,
  description: string,
  handler: () => void,
): UseReligionButtonModelResult {
  const controller = useMemo(
    () => getController(getPraiseControllerCtor),
    [],
  );
  const createOpts = useCallback(
    () => ({ name, description, handler }),
    [name, description, handler],
  );
  const { snap, modelRef } = useButtonModel(
    controller,
    createOpts,
    toModernSnapshot,
    snapshotEq,
  );
  const getTooltip = useCallback(
    () => makeGetTooltip(controller, createOpts)(),
    [controller, createOpts],
  );
  return { snap, modelRef, controller, getTooltip };
}

// ---------------------------------------------------------------------------
// Transform / MultiLink（sacrifice alicorns / refine TCs）
// ---------------------------------------------------------------------------

function getTransformControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    classes?: { ui?: { religion?: { TransformBtnController?: ControllerCtor } } };
  };
  return w.classes?.ui?.religion?.TransformBtnController;
}

function toMultiLinkSnapshot(model: Record<string, unknown>): MultiLinkSnapshot {
  const base = toModernSnapshot(model);
  const pick = (key: string): LinkSnapshot | null => {
    const link = model[key] as
      | { title?: string; visible?: boolean; handler?: (event: MouseEvent) => void }
      | undefined;
    if (!link) {return null;}
    return {
      title: String(link.title ?? ""),
      visible: link.visible !== false,
      handler: (event: MouseEvent) => link.handler?.(event),
    };
  };
  // 旧 MultiLinkBtn.renderLinks 按 all/half/fifth 顺序 addLink（core.js:2207-2211）。
  // links 均 float:right，source 顺序决定从右往左排列，因此数组需保持 all/half/fifth
  // 才能与旧版视觉一致（左 fifth / 中 half / 右 all）。
  const links: LinkSnapshot[] = [
    pick("allLink"),
    pick("halfLink"),
    pick("fifthLink"),
  ].filter((l): l is LinkSnapshot => l !== null);
  return { ...base, links };
}

export function useTransformButtonModel(
  name: string,
  description: string,
  prices: { name: string; val: number }[],
  controller: ModernButtonController | null,
  extra: Record<string, unknown> = {},
): UseMultiLinkButtonModelResult {
  const createOpts = useCallback(
    () => ({ name, description, prices, ...extra }),
    [name, description, prices, extra],
  );
  const { snap, modelRef } = useButtonModel(
    controller,
    createOpts,
    toMultiLinkSnapshot,
    multiLinkSnapshotEq,
  );
  const getTooltip = useCallback(
    () => makeGetTooltip(controller, createOpts)(),
    [controller, createOpts],
  );
  return { snap, modelRef, controller, getTooltip };
}

/** 使用游戏预创建的 unicornSacController */
export function useUnicornSacrificeButtonModel(
  name: string,
  description: string,
  prices: { name: string; val: number }[],
): UseMultiLinkButtonModelResult {
  const controller = useMemo(() => {
    const game = getGame() as { religion?: { unicornSacController?: ModernButtonController } } | null;
    return game?.religion?.unicornSacController ?? null;
  }, []);
  return useTransformButtonModel(name, description, prices, controller, {
    gainedResource: "tears",
    logTextID: "religion.sacrificeBtn.sacrifice.msg",
    logfilterID: "unicornSacrifice",
  });
}

/** 新建 TransformBtnController（alicorns / refine TC） */
export function useTransformButtonModelWithCtor(
  name: string,
  description: string,
  prices: { name: string; val: number }[],
  extra: Record<string, unknown>,
): UseMultiLinkButtonModelResult {
  const controller = useMemo(
    () => getController(getTransformControllerCtor, extra),
    [extra],
  );
  return useTransformButtonModel(name, description, prices, controller, extra);
}

// ---------------------------------------------------------------------------
// Refine Tears
// ---------------------------------------------------------------------------

function getRefineTearsControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    classes?: { ui?: { religion?: { RefineTearsBtnController?: ControllerCtor } } };
  };
  return w.classes?.ui?.religion?.RefineTearsBtnController;
}

function toRefineSnapshot(model: Record<string, unknown>): RefineLinksSnapshot {
  const base = toModernSnapshot(model);
  const pick = (key: string): LinkSnapshot | null => {
    const link = model[key] as
      | { title?: string; visible?: boolean; handler?: (event: MouseEvent) => void }
      | undefined;
    if (!link) {return null;}
    return {
      title: String(link.title ?? ""),
      visible: link.visible !== false,
      handler: (event: MouseEvent) => link.handler?.(event),
    };
  };
  // 旧 RefineBtn.renderLinks 按 hundred/twentyFive/five 顺序 addLink（core.js:2525-2529）。
  // 同 MultiLinkBtn，float:right 下 source 顺序决定视觉从右往左，保持旧 DOM 顺序。
  const links: LinkSnapshot[] = [
    pick("hundredLink"),
    pick("twentyFiveLink"),
    pick("fiveLink"),
  ].filter((l): l is LinkSnapshot => l !== null);
  return { ...base, links };
}

function refineSnapshotEq(
  a: RefineLinksSnapshot | null,
  b: RefineLinksSnapshot | null,
): boolean {
  if (a === b) {return true;}
  if (!a || !b) {return false;}
  return snapshotEq(a, b) && linksEq(a.links, b.links);
}

export function useRefineTearsButtonModel(
  name: string,
  description: string,
  prices: { name: string; val: number }[],
): UseMultiLinkButtonModelResult {
  const controller = useMemo(
    () => getController(getRefineTearsControllerCtor, { updateVisible: (model: { visible: boolean }) => { model.visible = true; } }),
    [],
  );
  const createOpts = useCallback(
    () => ({ name, description, prices }),
    [name, description, prices],
  );
  const { snap, modelRef } = useButtonModel(
    controller,
    createOpts,
    toRefineSnapshot,
    refineSnapshotEq,
  );
  const getTooltip = useCallback(
    () => makeGetTooltip(controller, createOpts)(),
    [controller, createOpts],
  );
  return { snap, modelRef, controller, getTooltip };
}

// ---------------------------------------------------------------------------
// Ziggurat / Religion / Transcendence / Pacts 标准建筑按钮
// ---------------------------------------------------------------------------

function getZigguratControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    com?: { nuclearunicorn?: { game?: { ui?: { ZigguratBtnController?: ControllerCtor } } } };
  };
  return w.com?.nuclearunicorn?.game?.ui?.ZigguratBtnController;
}

export function useZigguratButtonModel(
  upgradeName: string,
): UseReligionButtonModelResult {
  const controller = useMemo(
    () => getController(getZigguratControllerCtor),
    [],
  );
  const createOpts = useCallback(() => {
    const game = getGame() as GamePage & { religion?: { getZU?: (name: string) => { label?: string; description?: string; prices?: { name: string; val: number }[] } | null } } | null;
    const meta = game?.religion?.getZU?.(upgradeName);
    return {
      id: upgradeName,
      name: meta?.label ?? upgradeName,
      description: meta?.description ?? "",
      prices: meta?.prices ?? [],
    };
  }, [upgradeName]);
  const { snap, modelRef } = useButtonModel(
    controller,
    createOpts,
    toModernSnapshot,
    snapshotEq,
  );
  const getTooltip = useCallback(
    () => makeGetTooltip(controller, createOpts)(),
    [controller, createOpts],
  );
  return { snap, modelRef, controller, getTooltip };
}

// ---------------------------------------------------------------------------
// Adore / Transcend
// ---------------------------------------------------------------------------

function getResetFaithControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    com?: { nuclearunicorn?: { game?: { ui?: { ResetFaithBtnController?: ControllerCtor } } } };
  };
  return w.com?.nuclearunicorn?.game?.ui?.ResetFaithBtnController;
}

export function useAdoreButtonModel(
  name: string,
  description: string,
  handler: () => void,
): UseReligionButtonModelResult {
  const controller = useMemo(
    () => getController(getResetFaithControllerCtor),
    [],
  );
  const createOpts = useCallback(
    () => ({ name, description, handler }),
    [name, description, handler],
  );
  const { snap, modelRef } = useButtonModel(
    controller,
    createOpts,
    toModernSnapshot,
    snapshotEq,
  );
  const getTooltip = useCallback(
    () => makeGetTooltip(controller, createOpts)(),
    [controller, createOpts],
  );
  return { snap, modelRef, controller, getTooltip };
}

function getTranscendControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    com?: { nuclearunicorn?: { game?: { ui?: { TranscendBtnController?: ControllerCtor } } } };
  };
  return w.com?.nuclearunicorn?.game?.ui?.TranscendBtnController;
}

export function useTranscendButtonModel(
  name: string,
  description: string,
  handler: () => void,
): UseReligionButtonModelResult {
  const controller = useMemo(
    () => getController(getTranscendControllerCtor),
    [],
  );
  const createOpts = useCallback(
    () => ({ name, description, handler }),
    [name, description, handler],
  );
  const { snap, modelRef } = useButtonModel(
    controller,
    createOpts,
    toModernSnapshot,
    snapshotEq,
  );
  const getTooltip = useCallback(
    () => makeGetTooltip(controller, createOpts)(),
    [controller, createOpts],
  );
  return { snap, modelRef, controller, getTooltip };
}

// ---------------------------------------------------------------------------
// Religion / Transcendence / Pacts 标准建筑按钮
// ---------------------------------------------------------------------------

function getReligionControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    com?: { nuclearunicorn?: { game?: { ui?: { ReligionBtnController?: ControllerCtor } } } };
  };
  return w.com?.nuclearunicorn?.game?.ui?.ReligionBtnController;
}

export function useReligionUpgradeButtonModel(
  upgradeName: string,
): UseReligionButtonModelResult {
  const controller = useMemo(
    () => getController(getReligionControllerCtor),
    [],
  );
  const createOpts = useCallback(() => {
    const game = getGame() as GamePage & { religion?: { getRU?: (name: string) => { label?: string; description?: string; prices?: { name: string; val: number }[] } | null } } | null;
    const meta = game?.religion?.getRU?.(upgradeName);
    return {
      id: upgradeName,
      name: meta?.label ?? upgradeName,
      description: meta?.description ?? "",
      prices: meta?.prices ?? [],
    };
  }, [upgradeName]);
  const { snap, modelRef } = useButtonModel(
    controller,
    createOpts,
    toModernSnapshot,
    snapshotEq,
  );
  const getTooltip = useCallback(
    () => makeGetTooltip(controller, createOpts)(),
    [controller, createOpts],
  );
  return { snap, modelRef, controller, getTooltip };
}

function getTranscendenceControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    com?: { nuclearunicorn?: { game?: { ui?: { TranscendenceBtnController?: ControllerCtor } } } };
  };
  return w.com?.nuclearunicorn?.game?.ui?.TranscendenceBtnController;
}

export function useTranscendenceUpgradeButtonModel(
  upgradeName: string,
): UseReligionButtonModelResult {
  const controller = useMemo(
    () => getController(getTranscendenceControllerCtor),
    [],
  );
  const createOpts = useCallback(() => {
    const game = getGame() as GamePage & { religion?: { getTU?: (name: string) => { label?: string; description?: string; prices?: { name: string; val: number }[] } | null } } | null;
    const meta = game?.religion?.getTU?.(upgradeName);
    return {
      id: upgradeName,
      name: meta?.label ?? upgradeName,
      description: meta?.description ?? "",
      prices: meta?.prices ?? [],
    };
  }, [upgradeName]);
  const { snap, modelRef } = useButtonModel(
    controller,
    createOpts,
    toModernSnapshot,
    snapshotEq,
  );
  const getTooltip = useCallback(
    () => makeGetTooltip(controller, createOpts)(),
    [controller, createOpts],
  );
  return { snap, modelRef, controller, getTooltip };
}

function getPactsControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    com?: { nuclearunicorn?: { game?: { ui?: { PactsBtnController?: ControllerCtor } } } };
  };
  return w.com?.nuclearunicorn?.game?.ui?.PactsBtnController;
}

export function usePactButtonModel(
  pactName: string,
): UseReligionButtonModelResult {
  const controller = useMemo(
    () => getController(getPactsControllerCtor),
    [],
  );
  const createOpts = useCallback(() => {
    const game = getGame() as GamePage & { religion?: { getPact?: (name: string) => { label?: string; description?: string; prices?: { name: string; val: number }[] } | null } } | null;
    const meta = game?.religion?.getPact?.(pactName);
    return {
      id: pactName,
      name: meta?.label ?? pactName,
      description: meta?.description ?? "",
      prices: meta?.prices ?? [],
    };
  }, [pactName]);
  const { snap, modelRef } = useButtonModel(
    controller,
    createOpts,
    toModernSnapshot,
    snapshotEq,
  );
  const getTooltip = useCallback(
    () => makeGetTooltip(controller, createOpts)(),
    [controller, createOpts],
  );
  return { snap, modelRef, controller, getTooltip };
}
